import React, { useEffect, useState } from 'react';
import { api } from '../../routes/Users/user';
import { useNavigate } from 'react-router-dom';

const SESSION_KEY = 'authSession';
const SESSION_DURATION_MINUTES = 360; // 6 horas en minutos

type LoginFormProps = {
  onLogin: () => void;
};

type Configuracion = {
  logo_primary_base64?: string;
  logo_secondary_base64?: string;
  institution_name?: string;
};

// Íconos en línea (no dependen de la versión de la librería de íconos del visor)
const IconoCorreo = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);
const IconoCandado = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const IconoOjo = ({ tachado }: { tachado: boolean }) => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
    {tachado && <path d="m3 3 18 18" />}
  </svg>
);
const IconoCarga = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 animate-spin" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M21 12a9 9 0 1 1-6.2-8.56" />
  </svg>
);

const LoginForm: React.FC<LoginFormProps> = ({ onLogin }) => {
  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [verContrasena, setVerContrasena] = useState(false);
  const [mayusculas, setMayusculas] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [configuration, setConfiguration] = useState({} as Configuracion);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(api + '/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ correo, contrasena }),
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data.message || 'Correo o contraseña incorrectos');
        setLoading(false);
        return;
      }

      const expiresAt = Date.now() + 12 * 60 * 60 * 1000;
      localStorage.setItem(
        'usuario',
        JSON.stringify({
          token: data.token,
          user: data.user,
        })
      );
      localStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          token: data.token,
          user: data.user,
          expiresAt,
        })
      );
      onLogin();
      navigate('/');
    } catch (error) {
      console.error('Error de login:', error);
      setError('No se pudo conectar con el servidor. Revise su conexión e intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const config = async () => {
      try {
        const response = await fetch(api + '/api/settings');
        const data = await response.json();

        setConfiguration(data || {});
      } catch (error) {
        console.error(error);
        return null;
      }
    };
    config();
  }, []);

  const escudo = configuration.logo_secondary_base64 || configuration.logo_primary_base64 || '/logo_med.png';
  const institucion = configuration.institution_name || 'MED Viewer';
  const detectarMayusculas = (e: React.KeyboardEvent<HTMLInputElement>) =>
    setMayusculas(e.getModifierState && e.getModifierState('CapsLock'));

  const campo =
    'w-full rounded-lg border border-[#2a403a] bg-[#050a09] py-3 pl-10 pr-3 text-sm text-white placeholder-gray-600 transition-colors focus:border-[#00bfa5] focus:outline-none focus:ring-2 focus:ring-[#00bfa5]/25 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#050a09] [&:-webkit-autofill]:[-webkit-text-fill-color:#fff]';

  return (
    <div className="flex min-h-screen bg-[#060b09] text-white">
      {/* Panel de la institución (solo en pantallas anchas) */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden border-r border-[#142823] bg-gradient-to-br from-[#0b1d18] via-[#081411] to-[#050a09] p-12 lg:flex">
        {/* Anillos de fondo, como cortes de un tomógrafo */}
        <div className="pointer-events-none absolute -right-40 top-1/2 h-[560px] w-[560px] -translate-y-1/2 rounded-full border border-[#00bfa5]/[0.05]" />
        <div className="pointer-events-none absolute -right-24 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full border border-[#00bfa5]/[0.07]" />
        <div className="pointer-events-none absolute -right-8 top-1/2 h-[280px] w-[280px] -translate-y-1/2 rounded-full border border-[#00bfa5]/10" />

        <div className="relative flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#00bfa5]">
          <span className="h-2 w-2 rounded-full bg-[#00bfa5]" />
          MED Viewer
        </div>

        <div className="relative max-w-md">
          <div className="mb-8 inline-flex rounded-2xl bg-white p-3 shadow-lg shadow-black/40">
            <img src={escudo} alt={institucion} className="h-24 w-24 object-contain" />
          </div>
          <h1 className="text-3xl font-bold leading-tight text-white">{institucion}</h1>
          <p className="mt-3 text-base leading-relaxed text-gray-400">
            Visor de imágenes médicas: estudios, informes y envío de resultados en un solo lugar.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-gray-300">
            {['Estudios del PACS al instante', 'Informe radiológico con dictado por voz', 'Acceso protegido por usuario'].map(t => (
              <li key={t} className="flex items-center gap-3">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00bfa5]/15 text-[#00bfa5]">
                  <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-gray-600">Uso exclusivo del personal autorizado.</p>
      </div>

      {/* Formulario */}
      <div className="flex w-full items-center justify-center px-4 py-10 lg:w-1/2">
        <div className="w-full max-w-sm">
          {/* En celular el escudo va arriba del formulario */}
          <div className="mb-8 flex flex-col items-center text-center lg:hidden">
            <div className="mb-4 rounded-2xl bg-white p-2.5 shadow-lg shadow-black/40">
              <img src={escudo} alt={institucion} className="h-16 w-16 object-contain" />
            </div>
            <p className="text-sm font-semibold text-gray-300">{institucion}</p>
          </div>

          <h2 className="text-2xl font-bold text-white">Iniciar sesión</h2>
          <p className="mt-1.5 text-sm text-gray-500">Ingrese con su cuenta para ver los estudios.</p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate={false}>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                Correo electrónico
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"><IconoCorreo /></span>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  placeholder="usuario@hospital.com"
                  value={correo}
                  onChange={e => setCorreo(e.target.value)}
                  className={campo}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                Contraseña
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"><IconoCandado /></span>
                <input
                  id="password"
                  type={verContrasena ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={contrasena}
                  onChange={e => setContrasena(e.target.value)}
                  onKeyUp={detectarMayusculas}
                  onKeyDown={detectarMayusculas}
                  className={`${campo} pr-11`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setVerContrasena(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-500 transition-colors hover:text-[#00bfa5]"
                  title={verContrasena ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-label={verContrasena ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <IconoOjo tachado={verContrasena} />
                </button>
              </div>
              {mayusculas && <p className="mt-1.5 text-xs text-amber-400">Bloq Mayús está activado.</p>}
            </div>

            {error && (
              <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#00bfa5] py-3 text-sm font-bold text-[#04140f] transition-colors hover:bg-[#00d9bb] focus:outline-none focus:ring-2 focus:ring-[#00bfa5]/40 disabled:cursor-wait disabled:opacity-70"
            >
              {loading && <IconoCarga />}
              {loading ? 'Ingresando…' : 'Ingresar'}
            </button>
          </form>

          <p className="mt-10 text-center text-xs text-gray-600">¿Olvidó su contraseña? Pida al administrador que la restablezca.</p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
